import { Component, EventEmitter, OnInit, ViewChild } from '@angular/core';
import { VueFormError, VueServerMessage, VueServerMessageType } from '@vue/vueframework';
import { ActivatedRoute, Router } from '@angular/router';
import { AccountService } from '../shared/services/account.service';
import { PasswordResetOptions } from '../shared/types/PasswordResetOptions';
import { User } from '../shared/types/User';
import { AppTranslationService } from '../app-translation.service';
import { ResetOptionType } from '../shared/types/ResetOptionType.enum';
import { SecurityQuestionAnswer } from '../shared/types/SecurityQuestionAnswer';
import { PasswordResetWindow } from '../shared/types/PasswordResetWindow';
import { SessionDataService } from '../shared/services/session-data.service';
import { AccountRecoveryUtil } from '../shared/utils/accountrecovery.utils';
import { NgForm } from '@angular/forms';
import { WebNGRedirectExtras } from '../shared/types/WebNGRedirectExtras';
import { HttpErrorResponse } from '@angular/common/http';

@Component({
  selector: 'app-password-reset-options',
  standalone: false,
  styleUrls: [ './password-reset-options.component.css' ],
  templateUrl: './password-reset-options.component.html'
})
export class PasswordResetOptionsComponent implements OnInit {
  @ViewChild('howForm', { static: true }) public howForm: NgForm;

  public readonly ACCOUNT_RECOVERY_BUNDLE_NAME = 'AccountRecovery';
  public messages: VueServerMessage[] = [];
  public formError = new VueFormError();
  public resetChoice: ResetOptionType;
  public resetOptionTypeEnum = ResetOptionType;
  public hasResetOptions = true;
  public translations = {
    byEmail: '',
    bySMS: '',
    bySecurityQuestions: '',
    noResetOptions: '',
    pageTitle: '',
    secQuestionError: '',
    submitFormError: '',
    unknownError: ''
  };
  public pwResetOptions: PasswordResetOptions = {
    email: '',
    questions: [],
    sms: ''
  };
  public securityQuestionsPostBody: SecurityQuestionAnswer = {
    securityQuestionAnswers: [],
    userName: ''
  };
  public user: User = {
    clientStudentID: '',
    email: '',
    firstName: '',
    lastName: '',
    userName: ''
  };
  public redirectToWebNGFormEventEmitter = new EventEmitter();
  public redirectExtras = new WebNGRedirectExtras();
  private readonly maxSecQuestionAttempts = 3;
  private securityQuestionAttempts = 0;

  public constructor(private route: ActivatedRoute,
    private router: Router,
    private accountService: AccountService,
    private appTranslationService: AppTranslationService,
    private sessionDataService: SessionDataService,
    private accountRecoveryUtil: AccountRecoveryUtil
  ) {}

  public ngOnInit(): void {
    if (!this.route.snapshot.params.clientCode) {
      this.accountRecoveryUtil.navigateToNotFound();
    } else if (!this.sessionDataService.getUser() || !this.sessionDataService.getUser().userName) {
      this.previous();
    } else if (this.route.snapshot.data.passwordResetOptions) {
      this.initValues();
      this.getTranslations();
      this.decideIfPreselectOption();
    } else {
      this.messages.push(
        new VueServerMessage(VueServerMessageType.Error, this.translations.unknownError, true, '1')
      );
    }
  }

  /**
   * Resets the forms and form data. Called when the candidate selects a different radio button for retrieval mode.
   */
  public resetForm(): void {
    this.messages.length = 0;
    this.formError = new VueFormError();
    this.securityQuestionsPostBody.securityQuestionAnswers.forEach((question) => {
      question.answer = '';
    });
  }

  /**
   * Resets form and errors and clears field.
   */
  public clearFields(): void {
    let hasBlank = false;
    if (this.resetChoice === this.resetOptionTypeEnum.SECURITY_QUESTION) {
      this.securityQuestionsPostBody.securityQuestionAnswers.forEach((question) => {
        if (!question.answer) {
          hasBlank = true;
        }
      });
    }
    if (!hasBlank) {
      this.formError = new VueFormError();
      this.howForm.form.reset({ resetChoice: this.resetChoice });
    }
  }

  /**
   * Submits the currently active form. Will handle form validation, error messages, and navigation.
   */
  public submit(): void {
    this.messages.length = 0;
    if (this.howForm.form.valid === true) {
      if (this.resetChoice === this.resetOptionTypeEnum.EMAIL) {
        this.accountService.sendEmail(this.user, this.route.snapshot.params.clientCode)
          .subscribe(() => {
            this.redirectExtras.message = {
              messageKey: 'passwordResetOptionsSentToEmail',
              params: [
                this.pwResetOptions.email
              ],
              status: 'SUCCESS'
            };
            this.returnToLogin();
          }, (error) => this.handleError(error));
      } else if (this.resetChoice === this.resetOptionTypeEnum.VERIFICATION_CODE) {
        this.accountService.sendVerificationCode(this.user, this.route.snapshot.params.clientCode)
          .subscribe(() => this.goToVerificationPage(), (error) => this.handleError(error));
      } else if (this.resetChoice === this.resetOptionTypeEnum.SECURITY_QUESTION) {
        this.securityQuestionAttempts += 1;
        if (this.securityQuestionAttempts <= this.maxSecQuestionAttempts) {
          this.accountService.lookupSecurityQuestions(
            this.securityQuestionsPostBody,
            this.route.snapshot.params.clientCode
          ).subscribe((response) => this.handleSecQuestionResponse(response),
            (error) => this.handleSecQuestionError(error));
        }
      }
    } else {
      if (!this.howForm.form.valid) {
        // find the first field in error
        const pageControls = Object.keys(this.howForm.form.controls);
        let i = 0;
        do {
          if (this.howForm.form.controls[ pageControls[ i ] ].invalid) {
            this.formError.fieldInError = pageControls[ i ];
          }
          i++;
        }
        while (this.formError.fieldInError === '');
      }
      this.messages.push(
        new VueServerMessage(VueServerMessageType.Error, this.translations.submitFormError, true, '1')
      );
      this.formError.invalidFormSubmit = true;
    }
  }

  public returnToLogin(): void {
    this.redirectToWebNGFormEventEmitter.emit(this.redirectExtras);
  }

  public previous(): void {
    // Make the userName field empty before navigating to RetrievePassword page, it should not be pre-populated
    if (this.sessionDataService.getUser()) {
      this.sessionDataService.getUser().userName = '';
    }
    this.accountRecoveryUtil.navigateToRetrievePassword(this.route.snapshot.params.clientCode);
  }

  /**
   * Gets resetOptions from snapshot, gets user from user stored in history and sorts security questions by sequence
   */
  private initValues(): void {
    this.user = this.sessionDataService.getUser();
    this.securityQuestionsPostBody.userName = this.user.userName;
    this.pwResetOptions = this.route.snapshot.data.passwordResetOptions;
    if (this.pwResetOptions.questions && this.pwResetOptions.questions.length > 0) {
      this.pwResetOptions.questions = this.pwResetOptions.questions.sort((x, y) => (x.sequence > y.sequence) ? 1 : -1);
      this.pwResetOptions.questions.forEach((q) => {
        this.securityQuestionsPostBody.securityQuestionAnswers.push({ answer: '', questionID: q.id });
      });
    }
  }

  /**
   * Decision to determine if a default choice is chosen when there is only one availble option.
   */
  /* eslint-disable-next-line complexity */
  private decideIfPreselectOption(): void {
    if (
      !(this.pwResetOptions.questions && this.pwResetOptions.questions.length > 0) &&
      !this.pwResetOptions.email &&
      this.pwResetOptions.sms
    ) {
      this.resetChoice = this.resetOptionTypeEnum.VERIFICATION_CODE;
    } else if (
      (this.pwResetOptions.questions && this.pwResetOptions.questions.length > 0) &&
      !this.pwResetOptions.email &&
      !this.pwResetOptions.sms
    ) {
      this.resetChoice = this.resetOptionTypeEnum.SECURITY_QUESTION;
    } else if (
      !(this.pwResetOptions.questions && this.pwResetOptions.questions.length > 0) &&
      this.pwResetOptions.email &&
      !this.pwResetOptions.sms
    ) {
      this.resetChoice = this.resetOptionTypeEnum.EMAIL;
    } else if (!(this.pwResetOptions.questions && this.pwResetOptions.questions.length > 0) &&
      !this.pwResetOptions.email &&
      !this.pwResetOptions.sms
    ) {
      this.hasResetOptions = false;
      this.messages.push(
        new VueServerMessage(VueServerMessageType.Error, this.translations.noResetOptions, false, '1', false, true)
      );
    }
  }

  /**
   * Handles default error scenarios from accountService endpoints
   * @param err The error response from accountService
   */
  private handleError(err: HttpErrorResponse): void {
    this.messages.length = 0;
    if (err.status === 400 || err.status === 404) {
      const errorMessage = this.appTranslationService.getTranslationFromErrorMessage(
        err.error,
        this.translations.unknownError
      );
      this.messages.push(
        new VueServerMessage(VueServerMessageType.Error, errorMessage, true, '1')
      );
    } else {
      this.messages.push(
        new VueServerMessage(VueServerMessageType.Error, this.translations.unknownError, true, '1')
      );
    }
  }

  private handleSecQuestionResponse(resp: PasswordResetWindow): void {
    if (resp.id && resp.hash) {
      this.accountRecoveryUtil.navigateToPasswordUpdate(this.route.snapshot.params.clientCode, resp);
    } else {
      this.handleSecQuestionError(null);
    }
  }

  /**
   * Separate function from handleError() that gets specific message for security question failure
   * @param error The error is expected to be from when user answered security question incorrectly
   */
  private handleSecQuestionError(error: HttpErrorResponse): void {
    this.clearFields();
    if (this.securityQuestionAttempts === this.maxSecQuestionAttempts) {
      const newrelic = this.accountRecoveryUtil.getNewRelic();
      if (newrelic) {
        newrelic.addPageAction('invalidSecurityQuestionAnswersAction', {
          attempts: this.securityQuestionAttempts,
          clientCode: this.route.snapshot.params.clientCode,
          username: this.user.userName
        });
      }
      this.redirectExtras.message = {
        messageKey: 'maximumNumberFailedSecurityQuestionAnswersReached',
        status: 'FAILURE'
      };
      this.returnToLogin();
    } else if (error && error.status === 400) {
      this.translations.secQuestionError = this.appTranslationService.getTranslation(this.ACCOUNT_RECOVERY_BUNDLE_NAME,
        'error.SecurityQuestionError', [ (this.maxSecQuestionAttempts - this.securityQuestionAttempts) ]);
      this.messages.push(
        new VueServerMessage(VueServerMessageType.Error, this.translations.secQuestionError, true, '1')
      );
    } else {
      this.messages.push(
        new VueServerMessage(VueServerMessageType.Error, this.translations.unknownError, true, '1')
      );
    }
  }

  private getTranslations(): void {
    this.translations.pageTitle = this.appTranslationService.getTranslation(this.ACCOUNT_RECOVERY_BUNDLE_NAME,
      'PasswordResetOptionsComponent.PageTitle');
    this.translations.bySMS = this.appTranslationService.getTranslation(this.ACCOUNT_RECOVERY_BUNDLE_NAME,
      'PasswordResetOptionsComponent.ByVerificationCode', [ this.pwResetOptions.sms ]);
    this.translations.byEmail = this.appTranslationService.getTranslation(this.ACCOUNT_RECOVERY_BUNDLE_NAME,
      'PasswordResetOptionsComponent.ByEmail', [ this.pwResetOptions.email ]);
    this.translations.bySecurityQuestions = this.appTranslationService.getTranslation(this.ACCOUNT_RECOVERY_BUNDLE_NAME,
      'PasswordResetOptionsComponent.BySecurityQuestions');
    this.translations.submitFormError = this.appTranslationService.getTranslation(this.ACCOUNT_RECOVERY_BUNDLE_NAME,
      'error.GenericFormError');
    this.translations.unknownError = this.appTranslationService.getTranslation(this.ACCOUNT_RECOVERY_BUNDLE_NAME,
      'error.UnknownError');
    this.translations.noResetOptions = this.appTranslationService.getTranslation(this.ACCOUNT_RECOVERY_BUNDLE_NAME,
      'error.NoResetOptions', [ this.sessionDataService.getLoginURL(),
        this.appTranslationService.getTranslation(this.ACCOUNT_RECOVERY_BUNDLE_NAME, 'customerServiceUrl') ]);
  }

  private goToVerificationPage(): void {
    this.accountRecoveryUtil.navigateToVerify(this.route.snapshot.params.clientCode);
  }
}

